import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BACKEND_URL } from "../data/config";

const EmployeesPage = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  // Modals state
  const [editModal, setEditModal] = useState({ open: false, employee: null, name: "" });
  const [deleteModal, setDeleteModal] = useState({ open: false, employee: null });

  // Load employees
  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/employees-list.php`);
      if (response.data.success) {
        setEmployees(response.data.data);
        setPagination((prev) => ({
          ...prev,
          total: response.data.data.length,
          pages: Math.ceil(response.data.data.length / prev.limit)
        }));
      }
    } catch (error) {
      console.error(error);
      setEmployees([]);
      setPagination({ page: 1, limit: 10, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredEmployees = employees.filter(emp => emp.name.toLowerCase().includes(search.toLowerCase()));
  const paginatedEmployees = filteredEmployees.slice((pagination.page-1)*pagination.limit, pagination.page*pagination.limit);

  const handleSearch = (e) => { setSearch(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); };
  const handlePageChange = (page) => setPagination(prev => ({ ...prev, page }));

  // Open edit modal
  const openEditModal = (emp) => {
    setEditModal({ open: true, employee: emp, name: emp.name });
  };

  // Save edited name
  const saveEdit = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/update-employee.php`, {
        id: editModal.employee.id,
        name: editModal.name
      });
      if (response.data.success) {
        loadData();
        setEditModal({ open: false, employee: null, name: "" });
      } else alert(response.data.message);
    } catch (error) {
      console.error(error);
    }
  };

  // Open delete modal
  const openDeleteModal = (emp) => setDeleteModal({ open: true, employee: emp });

  // Confirm deletion
  const confirmDelete = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/delete-employee.php`, {
        id: deleteModal.employee.id
      });
      if (response.data.success) {
        loadData();
        setDeleteModal({ open: false, employee: null });
      } else alert(response.data.message);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      <div className="max-w-6xl mx-auto p-6">

        {/* HEADER */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Employees List</h1>
          <p className="text-sm text-gray-500 mt-1">
            {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? "s" : ""} found
          </p>
        </div>

        {/* SEARCH */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by employee name..."
            value={search}
            onChange={handleSearch}
            className="w-full md:w-80 border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded shadow-sm overflow-x-auto">
          <table className="w-full table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Full Name</th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : paginatedEmployees.length ? (
                paginatedEmployees.map(emp => (
                  <tr key={emp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{emp.id}</td>
                    <td className="px-4 py-2">{emp.name}</td>
                    <td className="px-4 py-2 space-x-2">
                      <button onClick={() => navigate(`/employee-attendance/${emp.id}`)} className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded border text-sm">View Attendance</button>
                      <button onClick={() => openEditModal(emp)} className="text-green-600 hover:text-green-900 px-3 py-1 rounded border text-sm">Edit</button>
                      <button onClick={() => openDeleteModal(emp)} className="text-red-600 hover:text-red-900 px-3 py-1 rounded border text-sm">Delete</button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No employees found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {pagination.pages > 1 && (
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-gray-500">{filteredEmployees.length} records</div>
            <div className="flex gap-2">
              <button onClick={() => handlePageChange(pagination.page-1)} disabled={pagination.page===1} className="px-3 py-1 border rounded disabled:opacity-40">Previous</button>
              <span className="px-3 py-1">Page {pagination.page} / {pagination.pages}</span>
              <button onClick={() => handlePageChange(pagination.page+1)} disabled={pagination.page===pagination.pages} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {editModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">
            <div className="bg-white p-6 rounded shadow-md w-80">
              <h2 className="text-lg font-semibold mb-4">Edit Employee</h2>
              <input
                type="text"
                value={editModal.name}
                onChange={e => setEditModal(prev => ({ ...prev, name: e.target.value }))}
                className="w-full border px-3 py-2 rounded mb-4"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditModal({ open: false, employee: null, name: "" })} className="px-3 py-1 border rounded">Cancel</button>
                <button onClick={saveEdit} className="px-3 py-1 bg-green-600 text-white rounded">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {deleteModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center">
            <div className="bg-white p-6 rounded shadow-md w-80">
              <h2 className="text-lg font-semibold mb-4">Delete Employee</h2>
              <p>Are you sure you want to delete <span className="font-semibold">{deleteModal.employee.name}</span>?</p>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setDeleteModal({ open: false, employee: null })} className="px-3 py-1 border rounded">Cancel</button>
                <button onClick={confirmDelete} className="px-3 py-1 bg-red-600 text-white rounded">Delete</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default EmployeesPage;